import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ArrowLeftMuiIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightMuiIcon from "@mui/icons-material/ArrowRight";
import BrandCard from "./BrandCard";
import { shopPath } from "../utils/seoPaths";
import { fetchFeaturedBrands, setSelectedBrand } from "../redux/brandSlice";
import { setCurrentBrand } from "../redux/brandCartSlice";

const BrandFeaturedImageCarousel = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { featuredBrands, isFetching } = useSelector((state) => state.brands);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  useEffect(() => {
    dispatch(fetchFeaturedBrands());
  }, [dispatch]);

  useEffect(() => {
    const brandGrid = document.getElementById("brandGrid");
    if (!brandGrid) return;

    const checkScroll = () => {
      setShowLeftArrow(brandGrid.scrollLeft > 0);
      setShowRightArrow(
        brandGrid.scrollLeft < brandGrid.scrollWidth - brandGrid.clientWidth
      );
    };

    checkScroll();
    brandGrid.addEventListener("scroll", checkScroll);

    return () => {
      brandGrid.removeEventListener("scroll", checkScroll);
    };
  }, [featuredBrands]);

  const scrollBrands = (direction) => {
    const container = document.getElementById("brandGrid");
    const scrollAmount = direction * 300;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const handleBrandSelection = (brand) => {
    dispatch(setSelectedBrand(brand));
    dispatch(setCurrentBrand(brand._id));
    // The same canonical address BrandCard links to, so a tap and the link
    // the crawler follows land on one page.
    history.push(shopPath("brand", brand));
  };

  // A brand appears in the carousel only when:
  //   1. isActive — the brand account is live
  //   2. promotion.promoteOnLanding === true — explicitly enabled by an admin
  //      in Brand Management → Landing Promotion. This is the scalability gate:
  //      a brand can be listed/featured without necessarily being promoted here.
  const activeBrands = featuredBrands.filter(
    (brand) => brand.isActive && brand.promotion?.promoteOnLanding === true
  );

  // Don't render the section if not fetching and no active brands
  if (!isFetching && activeBrands.length === 0) {
    return null;
  }

  return (
    <Wrapper>
      <ContentContainer>
        <Header>
          <HeaderContent>
            <TitleWrapper>
              {/* <Icon>🏢</Icon> */}
              <Title>Featured Brands</Title>
            </TitleWrapper>
            <Subtitle>
              Browse products from top brands and get them delivered
            </Subtitle>
          </HeaderContent>
        </Header>

        <ProductGrid id="brandGrid">
          {isFetching
            ? Array.from(new Array(3)).map((_, index) => (
                <BrandCardSkeleton key={index} />
              ))
            : activeBrands.map((brand, index) => (
                <BrandCard
                  key={brand._id}
                  brand={brand}
                  onClick={() => handleBrandSelection(brand)}
                />
              ))}
        </ProductGrid>

        <LeftArrow
          style={{ display: showLeftArrow ? "flex" : "none" }}
          onClick={() => scrollBrands(-1)}
        >
          <ArrowLeftMuiIcon style={{ color: "white", fontSize: 25 }} />
        </LeftArrow>

        <RightArrow
          style={{ display: showRightArrow ? "flex" : "none" }}
          onClick={() => scrollBrands(1)}
        >
          <ArrowRightMuiIcon style={{ color: "white", fontSize: 25 }} />
        </RightArrow>

        {/* View All Button */}
        <MoreButtonWrapper>
          <MoreButton onClick={() => history.push("/brands")}>
            View All Brands
          </MoreButton>
        </MoreButtonWrapper>
      </ContentContainer>
    </Wrapper>
  );
};

export default BrandFeaturedImageCarousel;

// Styled Components
const Wrapper = styled.section`
  width: 100%;
  padding: 30px 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #1976d2, #42a5f5, #1976d2);
    background-size: 200% 100%;
    animation: shimmer 3s linear infinite;
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @media (max-width: 768px) {
    padding: 25px 0;
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
`;

const Header = styled.div`
  margin-bottom: 20px;
  text-align: center;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }
`;

const HeaderContent = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 10px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 800;
  color: #1976d2;
  margin: 0;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
`;

const Subtitle = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0;
  font-weight: 500;
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 0.85rem;
  }
`;

const ProductGrid = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 10px 0;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 12px;
    padding: 8px 0;
  }
`;

const BrandCardSkeleton = styled.div`
  flex: 0 0 auto;
  width: 320px;
  height: 280px;
  border-radius: 18px;
  margin: 0;
  background: linear-gradient(90deg, #e8e8e8 25%, #f4f4f4 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: loading 1.4s infinite;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #e0e0e0, #f0f0f0, #e0e0e0);
    background-size: 200% 100%;
    animation: shimmer 2s linear infinite;
  }

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: -200% 0;
    }
    100% {
      background-position: 200% 0;
    }
  }

  @media (max-width: 480px) {
    width: 300px;
    height: 180px;
    border-radius: 16px;
  }
`;

const LeftArrow = styled.button`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  border: 3px solid white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.4);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.6);
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    left: 5px;
  }
`;

const RightArrow = styled(LeftArrow)`
  left: auto;
  right: 10px;

  @media (max-width: 768px) {
    right: 5px;
  }
`;

const MoreButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;

  @media (max-width: 768px) {
    margin-top: 15px;
  }
`;

const MoreButton = styled.button`
  padding: 10px 32px;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white;
  border: none;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.3);
  text-transform: uppercase;
  letter-spacing: 0.6px;

  &:hover {
    background: linear-gradient(135deg, #1565c0 0%, #0d47a1 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(25, 118, 210, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 8px 24px;
    font-size: 0.85rem;
  }
`;
